import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import PhotoSlot from "@/components/PhotoSlot";
import VisionPillars from "@/components/VisionPillars";
import { Reveal, LineReveal, Chapter } from "@/components/Reveal";
import { useSettings } from "@/hooks/SettingsContext";
import { trackPlanVisitClick } from "@/lib/analytics";

const HISTORY = [
  {
    year: "1970",
    label: "Prescott, Arizona",
    text: "Wayman and Nelda Mitchell began pastoring a small, struggling church in Prescott - and God began to move among a generation of young people searching for truth, through coffee-house concerts and personal evangelism.",
  },
  {
    year: "1973",
    label: "The first church plant",
    text: "A young couple was trained in the local church and sent out to pioneer a new congregation - beginning a pattern of discipleship and church planting that would define the fellowship.",
  },
  {
    year: "1978",
    label: "Around the world",
    text: "The first international church was planted in Australia, followed by Mexico, Europe and eventually more than 140 nations.",
  },
  {
    year: "TODAY",
    label: "4,300+ churches",
    text: "Known by names like The Potter's House, The Door and Victory Chapel, Christian Fellowship Ministries now spans more than 4,300 churches worldwide - and Victory Chapel Clay is its newest chapter, right here in Clay County.",
  },
];

export default function About() {
  const { settings } = useSettings();
  const s = settings || {};
  return (
    <>
      <SEO
        title="About - Who We Are, Our Mission & Story"
        description="Meet Victory Chapel Clay - a Bible-centered church plant in Middleburg, FL. Our story, mission, and heart for Clay County families. Launching January 2027."
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Victory Chapel Clay",
          alternateName: ["Victory Chapel Christian Church", "VC Clay"],
          url: "https://vcclaychurch.com/",
          logo: "https://vcclaychurch.com/vc-clay-logo-512.png",
          description: "A new Bible-centered Christian church plant in Middleburg, Florida, launching January 2027.",
          address: {
            "@type": "PostalAddress",
            streetAddress: "3180 CR 220 STE 6-7",
            addressLocality: "Middleburg",
            addressRegion: "FL",
            postalCode: "32068",
            addressCountry: "US",
          },
          email: "Victorychapelclay@gmail.com",
          telephone: "+1-904-383-2559",
        }}
      />
      <section className="grain relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">About</p>
          <LineReveal className="mt-4 font-display text-5xl font-900 tracking-tighter sm:text-7xl" lines={["A CHURCH FOR", "REAL PEOPLE."]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              We're new. We're local. And we're building something special in Clay County -
              and there's a place for your family here from the very beginning.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-28" data-testid="about-who-section">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 sm:gap-12 lg:grid-cols-2">
          <div>
            <Chapter number="01" eyebrow="Who We Are" title="NEW ON PURPOSE." />
            <Reveal delay={0.15}>
              <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
                Victory Chapel is a brand-new Christian church launching in the Lake Asbury / Middleburg area
                of Clay County, Florida. Being new isn't our weakness - it's our strength. Everyone here is
                building this church together, which means no insiders, no established circles, and no one
                who has "been here forever." We're all new.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                We're Bible-centered, family-oriented, and convinced that the people of Clay County deserve
                a church where they can actually know and be known.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-3xl shadow-lift">
              <img src="/you-belong-here.jpg" alt="Friends laughing together around a table with open Bibles" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" data-testid="about-community-photo" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-28" data-testid="about-mission-section">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <p className="font-display text-sm font-700 tracking-[0.25em] text-victory">02 - OUR MISSION</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-serif text-3xl italic leading-snug text-navy sm:text-4xl text-balance">
              "To help the people of Clay County encounter Jesus, build meaningful relationships,
              strengthen their families, and discover God's purpose for their lives."
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mx-auto mt-8 max-w-2xl font-serif text-lg italic leading-relaxed text-slate-600" data-testid="mission-verse">
              "Therefore go and make disciples of all nations, baptizing them in the name of the Father
              and of the Son and of the Holy Spirit."
            </p>
            <p className="mt-3 font-display text-xs font-700 uppercase tracking-[0.3em] text-victory">Matthew 28:19</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-28" data-testid="about-vision-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="text-center">
            <Reveal>
              <p className="font-display text-sm font-700 tracking-[0.25em] text-victory">03 - OUR VISION</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mx-auto mt-5 max-w-3xl font-display text-4xl font-800 tracking-tight text-navy sm:text-5xl text-balance">
                A LOCAL CHURCH WITH A WORLDWIDE VISION.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600" data-testid="vision-text">
                {s.vision_text} And it begins by reaching into the community we call home.
              </p>
            </Reveal>
          </div>
          <VisionPillars />
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-28" data-testid="about-story-section">
        <div className="mx-auto grid max-w-7xl items-start gap-10 px-5 sm:px-8 sm:gap-12 lg:grid-cols-2">
          <div>
            <Chapter number="04" eyebrow="Our Story" title="SOMETHING NEW IS BEGINNING." />
            <Reveal delay={0.15}>
              <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
                Victory Chapel is the story of a family saying yes to God - and of a community
                gaining a church built just for it.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
                After years of pastoral ministry, Pastor Daniel and Cassandra Richardson are planting
                their lives in the heart of Clay County to reach and be a blessing to this community -
                a church where real people encounter Jesus, families are strengthened, and no one walks alone.
              </p>
              <p className="mt-6 border-l-2 border-victory pl-5 font-serif text-lg italic leading-relaxed text-navy" data-testid="story-sent-out">
                Sent out from Victory Chapel of Jacksonville. Part of Christian Fellowship Ministries -
                a worldwide fellowship of more than 4,300 churches.
              </p>
            </Reveal>
          </div>
          <div>
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-navy/10 bg-white p-7 sm:p-8">
                <h3 className="font-display text-xl font-800 tracking-tight text-navy">WHAT WE BELIEVE</h3>
                <p className="mt-3 leading-relaxed text-slate-600" data-testid="beliefs-text">
                  {s.beliefs_text}
                </p>
                <Link to="/beliefs" data-testid="beliefs-full-cta"
                      className="mt-4 inline-flex items-center gap-1.5 font-display text-sm font-700 text-victory transition-colors hover:text-victory-dark">
                  READ OUR FULL STATEMENT OF FAITH <ArrowRight size={14} aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-28" data-testid="about-history-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Chapter number="05" eyebrow="Our History" title="A LEGACY THAT BEGAN LONG BEFORE US." />
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Victory Chapel Clay is part of Christian Fellowship Ministries - a worldwide fellowship
              born out of revival in a small church in Prescott, Arizona.
            </p>
          </Reveal>
          <div className="mt-10 sm:mt-12">
            {HISTORY.map((h, i) => (
              <Reveal key={h.year} delay={i * 0.06}>
                <div className="relative grid gap-2 border-l-2 border-navy/10 py-6 pl-6 sm:grid-cols-12 sm:gap-8 sm:pl-10" data-testid={`history-${h.year.toLowerCase()}`}>
                  <span className="absolute -left-[7px] top-8 h-3 w-3 rounded-full bg-victory" aria-hidden="true" />
                  <div className="sm:col-span-3">
                    <p className="font-display text-2xl font-800 tracking-tight text-victory">{h.year}</p>
                    <p className="mt-0.5 text-sm font-semibold text-navy">{h.label}</p>
                  </div>
                  <p className="text-base leading-relaxed text-slate-600 sm:col-span-9">{h.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy py-16 text-white sm:py-28" data-testid="about-pastor-section">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 sm:gap-12 lg:grid-cols-2">
          <Reveal>
            {s.pastor_family_photo_url ? (
              <img src={s.pastor_family_photo_url} alt="Pastor Daniel Richardson and his wife Cassandra with their children Gabriel, Layla and Amayah" className="w-full rounded-3xl shadow-lift" loading="lazy" />
            ) : (
              <PhotoSlot label="Pastor & family photo coming soon" dark />
            )}
          </Reveal>
          <div>
            <Reveal>
              <p className="font-display text-sm font-700 tracking-[0.25em] text-rose-300">06 - MEET OUR PASTOR</p>
              {s.pastor_name && <h2 className="mt-4 font-display text-4xl font-800 tracking-tight sm:text-5xl">{s.pastor_name}</h2>}
              {s.pastor_title && <p className="mt-1 text-sm text-white/60">{s.pastor_title}</p>}
              {!s.pastor_name && <h2 className="mt-4 font-display text-4xl font-800 tracking-tight sm:text-5xl">MEET OUR PASTOR</h2>}
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-6 font-serif text-xl italic leading-relaxed text-white/85">"{s.pastor_welcome}"</p>
              {s.pastor_bio && <p className="mt-5 text-base leading-relaxed text-white/70">{s.pastor_bio}</p>}
              <p className="mt-5 text-base leading-relaxed text-white/70">
                Their heart is simple: a church where every family in Clay County can know Jesus,
                grow in faith, and find their place.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <ol className="mt-8 border-l-2 border-white/15" data-testid="pastor-journey">
                {[
                  ["2008", "Daniel gives his life to Jesus, walking away from college sports and career plans to serve Him"],
                  ["2012", "Daniel marries Cassandra, who grew up in their home church"],
                  ["2016", "They answer the call to preach the gospel and pioneer their first church together"],
                  ["2022", "They return to Victory Chapel of Jacksonville as assistant and youth pastors"],
                  ["2027", "Sent out to plant Victory Chapel in the heart of Clay County"],
                ].map(([yr, txt]) => (
                  <li key={yr} className="relative py-3 pl-6" data-testid={`journey-${yr}`}>
                    <span className="absolute -left-[5px] top-5 h-2 w-2 rounded-full bg-victory" aria-hidden="true" />
                    <span className="font-display text-sm font-800 tracking-wide text-rose-300">{yr}</span>
                    <span className="ml-3 text-sm leading-relaxed text-white/75">{txt}</span>
                  </li>
                ))}
              </ol>
            </Reveal>
            <Reveal delay={0.24}>
              <Link to="/im-new" onClick={trackPlanVisitClick} data-testid="about-plan-visit-cta"
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-victory px-8 py-4 font-display text-base font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
                PLAN YOUR VISIT <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
